import { useEffect, useMemo, useRef, useState } from "react";
import {
  captureCompleteSchema,
  type CardCapture as Capture,
  type CardSummary,
  type CardTransport,
} from "@libs/buyer-identity-client";
import { captureHtml } from "@libs/buyer-identity-client/capture-html";

export function CardCapture({
  transport,
  cardId,
  onSaved,
}: {
  transport: CardTransport;
  cardId?: string;
  onSaved: (card: CardSummary) => void;
}) {
  const [capture, setCapture] = useState<Capture | null>(null),
    [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null),
    processing = useRef(false);
  const html = useMemo(() => (capture ? captureHtml(capture) : ""), [capture]);
  const [frameUrl, setFrameUrl] = useState("");
  useEffect(() => {
    if (!html) {
      setFrameUrl("");
      return;
    }
    // VGS uses location.origin for its frame messaging. about:srcdoc reports
    // "null" and silently stalls submission; a blob retains the host origin.
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    setFrameUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [html]);
  useEffect(() => {
    if (!capture) return;
    const listener = (event: MessageEvent) => {
      if (
        event.source !== frame.current?.contentWindow ||
        typeof event.data !== "string" ||
        processing.current
      )
        return;
      let message;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }
      if (
        message.type !== "lissen-card-capture" ||
        message.captureId !== capture.id
      )
        return;
      const parsed = captureCompleteSchema.safeParse(message.value);
      if (!parsed.success || parsed.data.captureId !== capture.id) return;
      processing.current = true;
      setBusy(true);
      void transport
        .completeCardCapture(parsed.data)
        .then((card) => {
          setCapture(null);
          onSaved(card);
        })
        .catch(() => {
          setCapture(null);
          setError("Could not save your card. Please try again.");
        })
        .finally(() => {
          processing.current = false;
          setBusy(false);
        });
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [capture, transport, onSaved]);
  return (
    <div>
      {capture ? (
        <>
          {frameUrl && (
            <iframe
              ref={frame}
              title="Secure card details"
              src={frameUrl}
              sandbox="allow-scripts allow-forms allow-same-origin"
              style={{
                width: "100%",
                height: capture.refresh ? 210 : 400,
                border: 0,
                borderRadius: 12,
              }}
            />
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => setCapture(null)}
          >
            Close card form
          </button>
        </>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError("");
            void transport
              .beginCardCapture(cardId)
              .then(setCapture)
              .catch(() =>
                setError("Could not open the card form. Please try again."),
              )
              .finally(() => setBusy(false));
          }}
        >
          {busy
            ? "Saving card…"
            : cardId
              ? "Confirm security code"
              : "Add a card"}
        </button>
      )}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
